import React, { useEffect, useState } from "react";
import { getOrderByID } from "../../../services/orderService";
import { Order } from "../../../interfaces/order";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getOrderFormByID,
  saveReferenceIdentificationForm,
} from "../../../services/orderFormService";
import { OrderForm } from "../../../interfaces/orderForm";
import { ReferenceOrder } from "../../../interfaces/referenceOrder";
import { getReferenceOrderByOrderID } from "../../../services/referenceOrderService";
import EducationalInstitutionForm, {
  EducationalInstitution,
} from "../../../components/form-components/EducationalInstitutionForm";
import AuthorizationForm, {
  Authorization,
} from "../../../components/form-components/AuthorizationForm";
import EmployerCongregationForm, {
  EmployerCongregation,
} from "../../../components/form-components/EmployerCongregationForm";
import BishopsReligiousForm, {
  BishopsReligious,
} from "../../../components/form-components/BishopsReligiousForm";
import LegalNamesForm, {
  LegalName,
} from "../../../components/form-components/LegalNamesForm";
import {
  getSafePickerDate,
  getStates,
  isValidBirthday,
  isValidDate,
  isValidEmail,
  isValidPhone,
  isValidSSN,
  isValidZip,
  toSafeDateString,
} from "../../../services/helpers";
import { getSimpleClient } from "../../../services/clientService";
import {
  deleteDraft,
  loadReferenceIdentificationDraft,
  saveDraftReferenceIdentification,
} from "../../../services/draftService";
import MonthDayYearPicker from "../../../components/component-tools/MonthDayYearPicker";
import { ReferenceAuthorizationInstruction } from "../../../interfaces/referenceAuthorizationInstruction";
import { getReferenceAuthorizationInstructions } from "../../../services/referenceAuthorizationService";

export interface ReferenceIdentification {
  firstName: string;
  middleName: string;
  lastName: string;
  title: string;
  residentialAddress: string;
  city: string;
  state: string;
  zipCode: string;
  phoneNumber: string;
  dateOfBirth?: Date;
  ssn: string;
  riMailingAddressDifferent: boolean;
  riMailingAddress: string;
  riMailingCity: string;
  riMailingState: string;
  riMailingZipCode: string;
}

export interface ReferenceIdentificationDraft {
  referenceIdentification: ReferenceIdentification;
  otherNameSelection: boolean | null;
  otherNamesList: LegalName[];
  educationalInstitutionList: EducationalInstitution[];
  uneducated: boolean | null;
  employerCongregationList: EmployerCongregation[];
  unemployed: boolean | null;
  bishopReligiousList: BishopsReligious[];
  unreligious: boolean | null;
  authorization: Authorization;
}

const ReferenceIdentificationForm = () => {
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const location = useLocation();
  const { orderID, orderFormID, anyCompletedForms } = location.state || {};
  const [successMessage, setSuccessMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [orderForm, setOrderForm] = useState<OrderForm | null>(null);
  const [referenceOrder, setReferenceOrder] = useState<ReferenceOrder | null>(
    null,
  );
  const [referenceIdentification, setReferenceIdentification] =
    useState<ReferenceIdentification>({
      firstName: "",
      middleName: "",
      lastName: "",
      title: "",
      residentialAddress: "",
      city: "",
      state: "",
      zipCode: "",
      phoneNumber: "",
      dateOfBirth: undefined,
      ssn: "",
      riMailingAddressDifferent: false,
      riMailingAddress: "",
      riMailingCity: "",
      riMailingState: "",
      riMailingZipCode: "",
    });
  const [educationalInstitutionList, setEducationalInstitutionList] = useState<
    EducationalInstitution[]
  >([
    {
      eiInstitutionName: "",
      eiContactPerson: "",
      eiConfidentialName: false,
      eiEmail: "",
      eiPhone: "",
      eiAddress: "",
      eiCity: "",
      eiState: "",
      eiZipCode: "",
      eiFromDate: new Date(),
      eiToDate: new Date(),
      eiCurrent: false,
      eiMailingAddressDifferent: false,
      eiMailingAddress: "",
      eiMailingCity: "",
      eiMailingState: "",
      eiMailingZipCode: "",
    },
  ]);
  const [authorization, setAuthorization] = useState<Authorization>({
    checked: false,
    dated: new Date(),
    aCity: "",
    aState: "",
    fullName: "",
  });
  const [employerCongregationList, setEmployerCongregationList] = useState<
    EmployerCongregation[]
  >([
    {
      employerName: "",
      ecContactPerson: "",
      ecConfidentialName: false,
      ecEmail: "",
      ecPhone: "",
      ecAddress: "",
      ecCity: "",
      ecState: "",
      ecZipCode: "",
      yourPosition: "",
      ecFromDate: new Date(),
      ecToDate: new Date(),
      ecCurrent: false,
      ecMailingAddressDifferent: false,
      ecMailingAddress: "",
      ecMailingCity: "",
      ecMailingState: "",
      ecMailingZipCode: "",
    },
  ]);
  const [bishopReligiousList, setBishopReligiousList] = useState<
    BishopsReligious[]
  >([
    {
      bishopName: "",
      diocese: "",
      brEmail: "",
      brPhone: "",
      brAddress: "",
      brCity: "",
      brState: "",
      brZipCode: "",
      brFromDate: new Date(),
      brToDate: new Date(),
      brCurrent: false,
      brMailingAddressDifferent: false,
      brMailingAddress: "",
      brMailingCity: "",
      brMailingState: "",
      brMailingZipCode: "",
    },
  ]);
  const [otherNamesList, setOtherNamesList] = useState<LegalName[]>([]);
  const [otherNameSelection, setOtherNameSelection] = useState<boolean | null>(
    null,
  );
  const states = getStates();
  const [uneducated, setUneducated] = useState(false);
  const [unemployed, setUnemployed] = useState(false);
  const [unreligious, setUnreligious] = useState(false);
  const [instructions, setInstructions] =
    useState<ReferenceAuthorizationInstruction>();

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const order = await getOrderByID(orderID);
        setOrder(order);
        const orderForm = await getOrderFormByID(orderFormID);
        setOrderForm(orderForm);

        const referenceOrder = await getReferenceOrderByOrderID(order?.orderID);
        setReferenceOrder(referenceOrder);

        const clientResponse = await getSimpleClient(order.clientID);

        const refAuthInstructionsResponse =
          await getReferenceAuthorizationInstructions(
            clientResponse.clientID,
            referenceOrder?.years,
          );
        setInstructions(refAuthInstructionsResponse);

        if (orderForm) {
          const draft = await loadReferenceIdentificationDraft(
            order.orderID,
            orderForm.orderFormID,
          );
          if (draft) {
            setReferenceIdentification(draft.referenceIdentification);
            setOtherNameSelection(draft.otherNameSelection);
            setOtherNamesList(draft.otherNamesList);
            setEducationalInstitutionList(draft.educationalInstitutionList);
            setUneducated(draft.uneducated ?? false);
            setEmployerCongregationList(draft.employerCongregationList);
            setUnemployed(draft.unemployed ?? false);
            setBishopReligiousList(draft.bishopReligiousList);
            setUnreligious(draft.unreligious ?? false);
            setAuthorization(draft.authorization);
          }
        }

        if (anyCompletedForms) {
          setReferenceIdentification((prev) => ({
            ...prev,
            title: order.candidateTitle ?? "",
            firstName: order.candidateFirstName ?? "",
            middleName: order.candidateMI ?? "",
            lastName: order.candidateLastName ?? "",
            dateOfBirth: order.candidateDOB ?? new Date(),
            ssn: order.candidateSSN ?? "",
            residentialAddress: order.candidateAddress ?? "",
            city: order.candidateCity ?? "",
            state: order.candidateState ?? "",
            zipCode: order.candidateZipCode ?? "",
            phoneNumber: order.candidatePhone ?? "",
          }));
        }
      } catch (error) {
        console.error("Error fetching candidate, order or orderForm:", error);
      } finally {
        setLoading(false);
      }
    };
    loadOrder();
  }, [orderFormID, orderID, anyCompletedForms]);

  // Draft Pieces
  const buildDraftObject = (): ReferenceIdentificationDraft => {
    return {
      referenceIdentification,
      otherNameSelection,
      otherNamesList,
      educationalInstitutionList,
      uneducated,
      employerCongregationList,
      unemployed,
      bishopReligiousList,
      unreligious,
      authorization,
    };
  };

  const handleSaveDraft = async () => {
    try {
      setLoadingSpinner(true);
      if (!order || !orderForm) return;
      const draft: ReferenceIdentificationDraft = buildDraftObject();
      const response = await saveDraftReferenceIdentification(
        order?.orderID,
        orderForm?.orderFormID,
        draft,
      );
      if (response) {
        navigate("/candidate/needs-review", {
          state: { successMessage: "Draft saved successfully." },
        });
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      alert("An error occurred while saving your draft.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleAddAnotherEducationalInstitution = () => {
    setEducationalInstitutionList((prevList) => {
      const lastAddress =
        prevList.length > 0 ? prevList[prevList.length - 1] : null;
      return [
        ...prevList,
        {
          eiInstitutionName: "",
          eiContactPerson: "",
          eiConfidentialName: false,
          eiEmail: "",
          eiPhone: "",
          eiAddress: "",
          eiCity: "",
          eiState: "",
          eiZipCode: "",
          eiFromDate: lastAddress
            ? new Date(lastAddress.eiFromDate)
            : new Date(),
          eiToDate: lastAddress ? new Date(lastAddress.eiFromDate) : new Date(),
          eiCurrent: false,
          eiMailingAddressDifferent: false,
          eiMailingAddress: "",
          eiMailingCity: "",
          eiMailingState: "",
          eiMailingZipCode: "",
        },
      ];
    });

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors.uneducated;
      delete updatedErrors.eiLength;
      return updatedErrors;
    });
  };

  const handleAddAnotherEmployerCongregation = () => {
    setEmployerCongregationList((prevList) => {
      const lastAddress =
        prevList.length > 0 ? prevList[prevList.length - 1] : null;

      return [
        ...prevList,
        {
          employerName: "",
          ecContactPerson: "",
          ecConfidentialName: false,
          ecEmail: "",
          ecPhone: "",
          ecAddress: "",
          ecCity: "",
          ecState: "",
          ecZipCode: "",
          yourPosition: "",
          ecFromDate: lastAddress
            ? new Date(lastAddress.ecFromDate)
            : new Date(),
          ecToDate: lastAddress ? new Date(lastAddress.ecFromDate) : new Date(),
          ecCurrent: false,
          ecMailingAddressDifferent: false,
          ecMailingAddress: "",
          ecMailingCity: "",
          ecMailingState: "",
          ecMailingZipCode: "",
        },
      ];
    });

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors.unemployed;
      delete updatedErrors.ecLength;
      return updatedErrors;
    });
  };

  const handleAddAnotherBishopReligious = () => {
    setBishopReligiousList((prevList) => {
      const lastAddress =
        prevList.length > 0 ? prevList[prevList.length - 1] : null;

      return [
        ...prevList,
        {
          bishopName: "",
          diocese: "",
          brEmail: "",
          brPhone: "",
          brAddress: "",
          brCity: "",
          brState: "",
          brZipCode: "",
          brFromDate: lastAddress
            ? new Date(lastAddress.brFromDate)
            : new Date(),
          brToDate: lastAddress ? new Date(lastAddress.brFromDate) : new Date(),
          brCurrent: false,
          brMailingAddressDifferent: false,
          brMailingAddress: "",
          brMailingCity: "",
          brMailingState: "",
          brMailingZipCode: "",
        },
      ];
    });

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors.unreligious;
      delete updatedErrors.brLength;
      return updatedErrors;
    });
  };

  const handleDeleteEducationalInstitution = (index: number) => {
    setEducationalInstitutionList((prevList) =>
      prevList.filter((_, i) => i !== index),
    );

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach((key) => {
        if (
          key.startsWith(`institutionName-${index}`) ||
          key.startsWith(`contactPerson-${index}`) ||
          key.startsWith(`eiEmail-${index}`) ||
          key.startsWith(`eiPhone-${index}`) ||
          key.startsWith(`eiAddress-${index}`) ||
          key.startsWith(`eiCity-${index}`) ||
          key.startsWith(`eiState-${index}`) ||
          key.startsWith(`eiZipCode-${index}`) ||
          key.startsWith(`eiFromDate-${index}`) ||
          key.startsWith(`eiToDate-${index}`) ||
          key.startsWith(`eiMailingAddress-${index}`) ||
          key.startsWith(`eiMailingCity-${index}`) ||
          key.startsWith(`eiMailingState-${index}`) ||
          key.startsWith(`eiMailingZipCode-${index}`)
        ) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  const handleDeleteEmployerCongregation = (index: number) => {
    setEmployerCongregationList((prevList) =>
      prevList.filter((_, i) => i !== index),
    );

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach((key) => {
        if (
          key.startsWith(`employerName-${index}`) ||
          key.startsWith(`ecContactPerson-${index}`) ||
          key.startsWith(`ecEmail-${index}`) ||
          key.startsWith(`ecPhone-${index}`) ||
          key.startsWith(`ecAddress-${index}`) ||
          key.startsWith(`ecCity-${index}`) ||
          key.startsWith(`ecState-${index}`) ||
          key.startsWith(`ecZipCode-${index}`) ||
          key.startsWith(`yourPosition-${index}`) ||
          key.startsWith(`ecFromDate-${index}`) ||
          key.startsWith(`ecToDate-${index}`) ||
          key.startsWith(`ecMailingAddress-${index}`) ||
          key.startsWith(`ecMailingCity-${index}`) ||
          key.startsWith(`ecMailingState-${index}`) ||
          key.startsWith(`ecMailingZipCode-${index}`)
        ) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  const handleDeleteBishopReligious = (index: number) => {
    setBishopReligiousList((prevList) =>
      prevList.filter((_, i) => i !== index),
    );

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach((key) => {
        if (
          key.startsWith(`bishopName-${index}`) ||
          key.startsWith(`diocese-${index}`) ||
          key.startsWith(`brEmail-${index}`) ||
          key.startsWith(`brPhone-${index}`) ||
          key.startsWith(`brAddress-${index}`) ||
          key.startsWith(`brCity-${index}`) ||
          key.startsWith(`brState-${index}`) ||
          key.startsWith(`brZipCode-${index}`) ||
          key.startsWith(`brFromDate-${index}`) ||
          key.startsWith(`brToDate-${index}`) ||
          key.startsWith(`brMailingAddress-${index}`) ||
          key.startsWith(`brMailingCity-${index}`) ||
          key.startsWith(`brMailingState-${index}`) ||
          key.startsWith(`brMailingZipCode-${index}`)
        ) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
    index?: number,
  ) => {
    let value: string | boolean | Date =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    setValidationErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));

    if (typeof value === "string" && /^\d{4}-\d{2}$/.test(value)) {
      value = `${value}-01`;
    }

    if (index !== undefined) {
      if (
        educationalInstitutionList[index] &&
        field in educationalInstitutionList[index]
      ) {
        setValidationErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          delete updatedErrors[`${field}-${index}`];
          return updatedErrors;
        });
        setEducationalInstitutionList((prevList) => {
          const updatedList = [...prevList];
          updatedList[index] = { ...updatedList[index], [field]: value };
          return updatedList;
        });
      } else if (
        employerCongregationList[index] &&
        field in employerCongregationList[index]
      ) {
        setValidationErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          delete updatedErrors[`${field}-${index}`];
          return updatedErrors;
        });
        setEmployerCongregationList((prevList) => {
          const updatedList = [...prevList];
          updatedList[index] = { ...updatedList[index], [field]: value };
          return updatedList;
        });
      } else if (
        bishopReligiousList[index] &&
        field in bishopReligiousList[index]
      ) {
        setValidationErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          delete updatedErrors[`${field}-${index}`];
          return updatedErrors;
        });
        setBishopReligiousList((prevList) => {
          const updatedList = [...prevList];
          updatedList[index] = { ...updatedList[index], [field]: value };
          return updatedList;
        });
      } else if (otherNamesList[index] && field in otherNamesList[index]) {
        setValidationErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          delete updatedErrors[`${field}-${index}`];
          return updatedErrors;
        });
        setOtherNamesList((prevList) => {
          const updatedList = [...prevList];
          updatedList[index] = { ...updatedList[index], [field]: value };
          return updatedList;
        });
      }
    }

    if (field === "unreligious") {
      const isUnreligious = value as boolean;
      setUnreligious(isUnreligious);
      if (isUnreligious) {
        for (let i = bishopReligiousList.length - 1; i >= 0; i--) {
          handleDeleteBishopReligious(i);
        }
      }
    } else if (field === "uneducated") {
      const isUneducated = value as boolean;
      setUneducated(isUneducated);
      if (isUneducated) {
        for (let i = educationalInstitutionList.length - 1; i >= 0; i--) {
          handleDeleteEducationalInstitution(i);
        }
      }
    } else if (field === "unemployed") {
      const isUnemployed = value as boolean;
      setUnemployed(isUnemployed);
      if (isUnemployed) {
        for (let i = employerCongregationList.length - 1; i >= 0; i--) {
          handleDeleteEmployerCongregation(i);
        }
      }
    } else if (field in referenceIdentification) {
      setReferenceIdentification((prev) => ({
        ...prev,
        [field]: value,
      }));
    } else if (field in authorization) {
      setAuthorization((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const validateFields = async () => {
    const errors: { [key: string]: string } = {};

    // Reference Identification Validations
    if (!referenceIdentification?.firstName) {
      errors.firstName = "First name is required.";
    }
    if (!referenceIdentification?.lastName) {
      errors.lastName = "Last name is required.";
    }
    if (!referenceIdentification?.title) {
      errors.title = "Title is required.";
    }
    if (!referenceIdentification?.residentialAddress) {
      errors.residentialAddress = "Residential address is required.";
    }
    if (!referenceIdentification?.city) {
      errors.city = " City is required.";
    }
    if (!referenceIdentification?.state) {
      errors.state = "State is required.";
    }
    if (!referenceIdentification?.zipCode) {
      errors.zipCode = "ZipCode is required.";
    } else if (!isValidZip(referenceIdentification.zipCode)) {
      errors.zipCode = "Please enter a valid zip code.";
    }
    if (!referenceIdentification?.phoneNumber) {
      errors.phoneNumber = "Phone number is required.";
    } else if (!isValidPhone(referenceIdentification.phoneNumber)) {
      errors.phoneNumber = "Please enter a valid phone number.";
    }
    if (!isValidDate(referenceIdentification?.dateOfBirth)) {
      errors.dateOfBirth = "DOB is required and must be valid.";
    } else if (
      !isValidBirthday(referenceIdentification?.dateOfBirth ?? new Date())
    ) {
      errors.dateOfBirth = "Please check your birth date";
    }
    if (!referenceIdentification?.ssn) {
      errors.ssn = "SSN is required.";
    } else if (!isValidSSN(referenceIdentification?.ssn)) {
      errors.ssn = "Enter a valid SSN";
    }

    if (referenceIdentification.riMailingAddressDifferent) {
      if (!referenceIdentification.riMailingAddress)
        errors[`riMailingAddress`] = "Mailing address is required.";
      if (!referenceIdentification.riMailingCity)
        errors[`riMailingCity`] = "Mailing city is required.";
      if (!referenceIdentification.riMailingState)
        errors[`riMailingState`] = "Mailing state is required.";
      if (!referenceIdentification.riMailingZipCode) {
        errors[`riMailingZipCode`] = "Mailing zip code is required.";
      } else if (!isValidZip(referenceIdentification.riMailingZipCode)) {
        errors[`riMailingZipCode`] = "Please enter a valid zip code.";
      }
    }

    // Other Names Validations
    if (otherNameSelection === null) {
      errors.otherNameSelection = "Please select Yes or No.";
    } else if (otherNameSelection === true && otherNamesList.length === 0) {
      errors.otherNameSelection = "You must enter at least one other name.";
    }
    otherNamesList.forEach((name, index) => {
      if (otherNameSelection === true) {
        if (!name.lnFirstName)
          errors[`lnFirstName-${index}`] = "First name is required.";
        if (!name.lnLastName)
          errors[`lnLastName-${index}`] = "Last name is required.";
      }
    });

    // Educational Institution Validations
    educationalInstitutionList.forEach((institution, index) => {
      if (!institution.eiInstitutionName)
        errors[`eiInstitutionName-${index}`] = "Institution Name is required.";
      if (!institution.eiContactPerson)
        errors[`eiContactPerson-${index}`] = "Contact person is required.";
      if (!institution.eiEmail) {
        errors[`eiEmail-${index}`] = "Email is required.";
      } else if (!isValidEmail(institution.eiEmail)) {
        errors[`eiEmail-${index}`] = "Invalid email address.";
      }
      if (!institution.eiPhone) {
        errors[`eiPhone-${index}`] = "Phone is required.";
      } else if (!isValidPhone(institution.eiPhone)) {
        errors[`eiPhone-${index}`] = "Please enter a valid phone number.";
      }
      if (!institution.eiAddress)
        errors[`eiAddress-${index}`] = "Address is required.";
      if (!institution.eiCity) errors[`eiCity-${index}`] = "City is required.";
      if (!institution.eiState)
        errors[`eiState-${index}`] = "State is required.";
      if (!institution.eiZipCode) {
        errors[`eiZipCode-${index}`] = "Zip code is required.";
      } else if (!isValidZip(institution.eiZipCode)) {
        errors[`eiZipCode-${index}`] = "Please enter a valid zip code.";
      }
      if (!isValidDate(institution.eiFromDate)) {
        errors[`eiFromDate-${index}`] =
          "From date is required and must be valid.";
      }
      if (!isValidDate(institution.eiToDate)) {
        errors[`eiToDate-${index}`] = "To date is required and must be valid.";
      }
      if (institution.eiMailingAddressDifferent) {
        if (!institution.eiMailingAddress)
          errors[`eiMailingAddress-${index}`] = "Mailing address is required.";
        if (!institution.eiMailingCity)
          errors[`eiMailingCity-${index}`] = "Mailing city is required.";
        if (!institution.eiMailingState)
          errors[`eiMailingState-${index}`] = "Mailing state is required.";
        if (!institution.eiMailingZipCode) {
          errors[`eiMailingZipCode-${index}`] = "Mailing zip code is required.";
        } else if (!isValidZip(institution.eiMailingZipCode)) {
          errors[`eiMailingZipCode-${index}`] =
            "Please enter a valid zip code.";
        }
      }
    });

    if (educationalInstitutionList.length === 0 && uneducated === false) {
      errors.uneducated =
        "You must either add an educational institution or attest that you have not attended any for the requested time period. ";
    }

    // Employer/Congregation Validations
    employerCongregationList.forEach((employer, index) => {
      if (!employer.employerName)
        errors[`employerName-${index}`] = "Employer name is required.";
      if (!employer.ecContactPerson)
        errors[`ecContactPerson-${index}`] = "Contact person is required.";
      if (!employer.ecEmail) {
        errors[`ecEmail-${index}`] = "Email is required.";
      } else if (!isValidEmail(employer.ecEmail)) {
        errors[`ecEmail-${index}`] = "Please enter a valid email address.";
      }
      if (!employer.ecPhone) {
        errors[`ecPhone-${index}`] = "Phone is required.";
      } else if (!isValidPhone(employer.ecPhone)) {
        errors[`ecPhone-${index}`] = "Please enter a valid phone number.";
      }
      if (!employer.ecAddress)
        errors[`ecAddress-${index}`] = "Address is required.";
      if (!employer.ecCity) errors[`ecCity-${index}`] = "City is required.";
      if (!employer.ecState) errors[`ecState-${index}`] = "State is required.";
      if (!employer.ecZipCode) {
        errors[`ecZipCode-${index}`] = "Zip code is required.";
      } else if (!isValidZip(employer.ecZipCode)) {
        errors[`ecZipCode-${index}`] = "Please enter a valid zip code.";
      }
      if (!employer.yourPosition)
        errors[`yourPosition-${index}`] = "Your position is required.";
      if (!isValidDate(employer.ecFromDate)) {
        errors[`ecFromDate-${index}`] =
          "From date is required and must be valid.";
      }
      if (!isValidDate(employer.ecToDate)) {
        errors[`ecToDate-${index}`] = "To date is required and must be valid.";
      }
      if (employer.ecMailingAddressDifferent) {
        if (!employer.ecMailingAddress)
          errors[`ecMailingAddress-${index}`] = "Mailing address is required.";
        if (!employer.ecMailingCity)
          errors[`ecMailingCity-${index}`] = "Mailing city is required.";
        if (!employer.ecMailingState)
          errors[`ecMailingState-${index}`] = "Mailing state is required.";
        if (!employer.ecMailingZipCode) {
          errors[`ecMailingZipCode-${index}`] = "Mailing zip code is required.";
        } else if (!isValidZip(employer.ecMailingZipCode)) {
          errors[`ecMailingZipCode-${index}`] =
            "Please enter a valid zip code.";
        }
      }
    });

    if (employerCongregationList.length < 2 && unemployed === false) {
      errors.unemployed =
        "You must add 2 employers/congregations or contact us before proceeding. ";
    }

    // Bishop/Religious Validations
    bishopReligiousList.forEach((bishop, index) => {
      if (!bishop.bishopName)
        errors[`bishopName-${index}`] = "Bishop name is required.";
      if (!bishop.diocese) errors[`diocese-${index}`] = "Diocese is required.";
      if (!bishop.brEmail) {
        errors[`brEmail-${index}`] = "Email is required.";
      } else if (!isValidEmail(bishop.brEmail)) {
        errors[`brEmail-${index}`] = "Please enter a valid email address.";
      }
      if (!bishop.brPhone) {
        errors[`brPhone-${index}`] = "Phone is required.";
      } else if (!isValidPhone(bishop.brPhone)) {
        errors[`brPhone-${index}`] = "Please enter a valid phone number.";
      }
      if (!bishop.brAddress)
        errors[`brAddress-${index}`] = "Address is required.";
      if (!bishop.brCity) errors[`brCity-${index}`] = "City is required.";
      if (!bishop.brState) errors[`brState-${index}`] = "State is required.";
      if (!bishop.brZipCode) {
        errors[`brZipCode-${index}`] = "Zip code is required.";
      } else if (!isValidZip(bishop.brZipCode)) {
        errors[`brZipCode-${index}`] = "Please enter a valid zip code.";
      }
      if (!isValidDate(bishop.brFromDate)) {
        errors[`brFromDate-${index}`] =
          "From date is required and must be valid.";
      }
      if (!isValidDate(bishop.brToDate)) {
        errors[`brToDate-${index}`] = "To date is required and must be valid.";
      }
      if (bishop.brMailingAddressDifferent) {
        if (!bishop.brMailingAddress)
          errors[`brMailingAddress-${index}`] = "Mailing address is required.";
        if (!bishop.brMailingCity)
          errors[`brMailingCity-${index}`] = "Mailing city is required.";
        if (!bishop.brMailingState)
          errors[`brMailingState-${index}`] = "Mailing state is required.";
        if (!bishop.brMailingZipCode) {
          errors[`brMailingZipCode-${index}`] = "Mailing zip code is required.";
        } else if (!isValidZip(bishop.brMailingZipCode)) {
          errors[`brMailingZipCode-${index}`] =
            "Please enter a valid zip code.";
        }
      }
    });

    if (bishopReligiousList.length === 0 && unreligious === false) {
      errors.unreligious =
        "You must either add a bishop or religious supervisor or attest that this does not apply to you. ";
    }

    // Authorization Validation
    if (!authorization.checked) {
      errors.checked = "You must agree before continuing.";
    }
    if (!isValidDate(authorization.dated)) {
      errors.dated = "Date is required and must be valid.";
    }
    if (!authorization.fullName) {
      errors.fullName = "Full name is required.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    setLoadingSpinner(true);

    const isValid = await validateFields();
    if (!isValid) {
      console.log("Validation failed. Please fix the errors.");
      setLoadingSpinner(false);
      const firstErrorElement = document.querySelector(".invalid-feedback");
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      return;
    }

    //validate dates
    if (referenceOrder) {
      try {
        if (order && orderForm?.orderFormID && referenceOrder) {
          const clientResponse = await getSimpleClient(order?.clientID);
          const result = await saveReferenceIdentificationForm(
            order,
            orderForm?.orderFormID,
            referenceIdentification,
            otherNamesList,
            educationalInstitutionList,
            employerCongregationList,
            bishopReligiousList,
            authorization,
            referenceOrder,
            clientResponse.diocese ? clientResponse.diocese : "",
            order.clientID,
          );

          if (result) {
            try {
              if (order && orderFormID)
                await deleteDraft(
                  order.orderID,
                  orderForm.orderFormID,
                  "referenceIdentification",
                );
            } catch (error) {
              console.warn(
                "Draft deletion failed or was already deleted:",
                error,
              );
            }
            navigate("/candidate/needs-review", {
              state: { successMessage: "Form successfully submitted." },
            });
          } else {
            alert(
              "We were unable to submit your form. Please check your entries and try again.",
            );
          }
        }
      } catch (error) {
        console.error("Error saving reference identification form:", error);
        alert("An error occurred while saving the form. Please try again.");
      } finally {
        setLoadingSpinner(false);
      }
    }
  };

  const handleYesNoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === "true";

    setOtherNameSelection(value);

    if (!value) {
      setOtherNamesList([]);
    }

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors.otherNameSelection;
      return updatedErrors;
    });
  };

  const handleAddAnotherOtherName = () => {
    setOtherNamesList((prevList) => [
      ...prevList,
      {
        lnFirstName: "",
        lnLastName: "",
        lnMiddleName: "",
        lnFromDate: new Date(),
        lnToDate: new Date(),
        confidential: false,
      },
    ]);

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors.otherNameSelection;
      return updatedErrors;
    });
  };

  const handleDeleteOtherName = (index: number) => {
    setOtherNamesList((prevList) => prevList.filter((_, i) => i !== index));

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach((key) => {
        if (
          key.startsWith(`lnFirstName-${index}`) ||
          key.startsWith(`lnLastName-${index}`) ||
          key.startsWith(`lnFromDate-${index}`) ||
          key.startsWith(`lnToDate-${index}`)
        ) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  return (
    <div className="content-container">
      <div className="form-header-container">
        <h1>
          {order?.candidateFirstName} {order?.candidateLastName}
        </h1>
        <div className="page-items-container"></div>
      </div>
      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <br></br>

          <div className="data-container">
            <div className="field-stacked" style={{ flex: "0 0 10%" }}>
              <label className="field-label">Title:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.title ? "is-invalid" : ""
                  }`}
                  type="text"
                  placeholder="Mr./Ms., The Rev., ..."
                  value={referenceIdentification.title || ""}
                  onChange={(e) => handleInputChange(e, "title")}
                />
                {validationErrors.title && (
                  <div className="invalid-feedback">
                    {validationErrors.title}
                  </div>
                )}
              </div>
            </div>

            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">First Legal Name:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.firstName ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={referenceIdentification.firstName || ""}
                  onChange={(e) => handleInputChange(e, "firstName")}
                />
                {validationErrors.firstName && (
                  <div className="invalid-feedback">
                    {validationErrors.firstName}
                  </div>
                )}
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Middle Legal Name:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={referenceIdentification?.middleName || ""}
                  onChange={(e) => handleInputChange(e, "middleName")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Last Legal Name:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.lastName ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={referenceIdentification?.lastName || ""}
                  onChange={(e) => handleInputChange(e, "lastName")}
                />
                {validationErrors.lastName && (
                  <div className="invalid-feedback">
                    {validationErrors.lastName}
                  </div>
                )}
              </div>
            </div>

            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Residential Address:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.residentialAddress ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={referenceIdentification.residentialAddress || ""}
                  onChange={(e) => handleInputChange(e, "residentialAddress")}
                />
                {validationErrors.residentialAddress && (
                  <div className="invalid-feedback">
                    {validationErrors.residentialAddress}
                  </div>
                )}
              </div>
            </div>

            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">City:</label>
              <input
                className={`form-control ${
                  validationErrors.city ? "is-invalid" : ""
                }`}
                type="text"
                value={referenceIdentification.city || ""}
                onChange={(e) => handleInputChange(e, "city")}
              />
              {validationErrors.city && (
                <div className="invalid-feedback">{validationErrors.city}</div>
              )}
            </div>

            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">State:</label>
              <select
                className={`form-control ${
                  validationErrors.state ? "is-invalid" : ""
                }`}
                value={referenceIdentification.state || ""}
                onChange={(e) => handleInputChange(e, "state")}
              >
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={state.abbreviation} value={state.abbreviation}>
                    {state.name} ({state.abbreviation})
                  </option>
                ))}
              </select>
              {validationErrors.state && (
                <div className="invalid-feedback">{validationErrors.state}</div>
              )}
            </div>

            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">ZipCode:</label>
              <input
                className={`form-control ${
                  validationErrors.zipCode ? "is-invalid" : ""
                }`}
                type="text"
                value={referenceIdentification.zipCode || ""}
                onChange={(e) => handleInputChange(e, "zipCode")}
              />
              {validationErrors.zipCode && (
                <div className="invalid-feedback">
                  {validationErrors.zipCode}
                </div>
              )}
            </div>

            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Phone Number:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.phoneNumber ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={referenceIdentification.phoneNumber || ""}
                  onChange={(e) => handleInputChange(e, "phoneNumber")}
                />
                {validationErrors.phoneNumber && (
                  <div className="invalid-feedback">
                    {validationErrors.phoneNumber}
                  </div>
                )}
              </div>
            </div>

            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Date of Birth:</label>
              <MonthDayYearPicker
                value={getSafePickerDate(referenceIdentification.dateOfBirth)}
                onChange={(newDate) =>
                  handleInputChange(
                    { target: { value: toSafeDateString(newDate) } } as any,
                    "dateOfBirth",
                  )
                }
              />
              {validationErrors.dateOfBirth && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {validationErrors.dateOfBirth}
                </div>
              )}
            </div>

            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">SSN:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.ssn ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={referenceIdentification.ssn || ""}
                  onChange={(e) => handleInputChange(e, "ssn")}
                />
                {validationErrors.ssn && (
                  <div className="invalid-feedback">{validationErrors.ssn}</div>
                )}
              </div>
            </div>

            <div className="field-long" style={{ flex: "0 0 100%" }}>
              <label className="field-label" style={{ textWrap: "wrap" }}>
                Mailing Address Different than Physical Address:
              </label>
              <div>
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={referenceIdentification.riMailingAddressDifferent}
                  onChange={(e) =>
                    handleInputChange(
                      e as React.ChangeEvent<HTMLInputElement>,
                      "riMailingAddressDifferent",
                    )
                  }
                />
              </div>
            </div>

            {referenceIdentification.riMailingAddressDifferent && (
              <>
                <div className="field-stacked" style={{ flex: "0 0 40%" }}>
                  <label className="field-label">Mailing Address:</label>
                  <input
                    className={`form-control ${
                      validationErrors[`riMailingAddress`] ? "is-invalid" : ""
                    }`}
                    type="text"
                    value={referenceIdentification.riMailingAddress || ""}
                    onChange={(e) => handleInputChange(e, "riMailingAddress")}
                  />
                  {validationErrors[`riMailingAddress`] && (
                    <div className="invalid-feedback">
                      {validationErrors[`riMailingAddress`]}
                    </div>
                  )}
                </div>

                <div className="field-stacked" style={{ flex: "0 0 40%" }}>
                  <label className="field-label">Mailing City:</label>
                  <input
                    className={`form-control ${
                      validationErrors[`riMailingCity`] ? "is-invalid" : ""
                    }`}
                    type="text"
                    value={referenceIdentification.riMailingCity || ""}
                    onChange={(e) => handleInputChange(e, "riMailingCity")}
                  />
                  {validationErrors[`riMailingCity`] && (
                    <div className="invalid-feedback">
                      {validationErrors[`riMailingCity`]}
                    </div>
                  )}
                </div>

                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Mailing State:</label>
                  <select
                    className={`form-select ${
                      validationErrors[`riMailingState`] ? "is-invalid" : ""
                    }`}
                    value={referenceIdentification.riMailingState || ""}
                    onChange={(e) => handleInputChange(e, "riMailingState")}
                  >
                    <option value="">Select State</option>
                    {states.map((state) => (
                      <option
                        key={state.abbreviation}
                        value={state.abbreviation}
                      >
                        {state.name} ({state.abbreviation})
                      </option>
                    ))}
                  </select>
                  {validationErrors[`riMailingState`] && (
                    <div className="invalid-feedback">
                      {validationErrors[`riMailingState`]}
                    </div>
                  )}
                </div>

                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Mailing Zip Code:</label>
                  <input
                    className={`form-control ${
                      validationErrors[`riMailingZipCode`] ? "is-invalid" : ""
                    }`}
                    type="text"
                    value={referenceIdentification.riMailingZipCode || ""}
                    onChange={(e) => handleInputChange(e, "riMailingZipCode")}
                  />
                  {validationErrors[`riMailingZipCode`] && (
                    <div className="invalid-feedback">
                      {validationErrors[`riMailingZipCode`]}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Other Names */}
          <div className="data-container">
            <div className="text-body" style={{ flex: "0 0 100%" }}>
              Other names used. (including maiden names, nicknames, and aliases)
            </div>
            <div className="field-long" style={{ flex: "0 0 10%" }}>
              <label className="field-label">Yes:</label>
              <input
                className="form-check-input"
                type="radio"
                name="additionalName"
                value="true"
                checked={otherNameSelection === true}
                onChange={(e) => handleYesNoChange(e)}
              />
            </div>
            <div className="field-long" style={{ flex: "0 0 10%" }}>
              <label className="field-label">No:</label>
              <input
                className="form-check-input"
                type="radio"
                name="additionalName"
                value="false"
                checked={otherNameSelection === false}
                onChange={(e) => handleYesNoChange(e)}
              />
            </div>
            {validationErrors.otherNameSelection && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {validationErrors.otherNameSelection}
              </div>
            )}
            <div style={{ flex: "0 0 70%" }}></div>

            {otherNameSelection && (
              <div style={{ flex: "0 0 100%" }}>
                <div className="text-body" style={{ flex: "0 0 100%" }}>
                  Other names you have used, including maiden name, nicknames,
                  and aliases and the year(s) your name(s) changed:
                </div>
                <br></br>
                {otherNamesList.map((otherName, index) => (
                  <LegalNamesForm
                    key={index}
                    legalName={otherName}
                    validationErrors={validationErrors}
                    onInputChange={handleInputChange}
                    onDelete={() => handleDeleteOtherName(index)}
                    index={index}
                    other={true}
                  />
                ))}
                <br></br>
                <div className="btn-center" style={{ flex: "0 0 100%" }}>
                  <button
                    className="oxbtn btn-purple"
                    onClick={() => handleAddAnotherOtherName()}
                  >
                    Add Another Name
                    <i className="bi bi-plus-lg btn-icon"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="text-form-header">
            IDENTIFICATION OF SCHOOLS, EMPLOYERS, SUBSTANTIAL SERVICES, AND
            BISHOPS/RELIGIOUS SUPERVISORS
          </div>
          <br></br>
          <div className="text-body">
            <div className="text-form-header">Instructions</div>
            <div>
              <ol className="alphabet-list">
                <li>{instructions?.instructionOne}</li>
                <li>{instructions?.instructionTwo}</li>
                <li>{instructions?.instructionThree}</li>
              </ol>
            </div>
            <div style={{ textAlign: "center" }}>
              <b>
                Complete and accurate information will expedite this process.
              </b>
            </div>
          </div>
          <br></br>

          {/* Educational Institutions */}
          <div className="data-container">
            <div
              className="text-form-header"
              style={{ flex: "0 0 100%", textAlign: "left" }}
            >
              Educational Institutions
            </div>
            <ol className="alphabet-list">
              <li>{instructions?.instructionOne}</li>
            </ol>
            {validationErrors.eiLength && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {validationErrors.eiLength}
              </div>
            )}
            <div className="field-long" style={{ flex: "0 0 100%" }}>
              <div className="field-input" style={{ flex: "0 0 3%" }}>
                <input
                  className={`form-check-input ${
                    validationErrors.uneducated ? "is-invalid" : ""
                  }`}
                  type="checkbox"
                  checked={uneducated}
                  onChange={(e) => handleInputChange(e, "uneducated")}
                />
              </div>
              <label className="field-label" style={{ textWrap: "wrap" }}>
                I have not attended any education institutions during the
                requested time period
              </label>
            </div>
            <div style={{ flex: "0 0 100%" }}>
              {validationErrors.uneducated && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {validationErrors.uneducated}
                </div>
              )}
            </div>
            {educationalInstitutionList.map((institution, index) => (
              <EducationalInstitutionForm
                key={index}
                educationalInstitution={institution}
                validationErrors={validationErrors}
                onInputChange={handleInputChange}
                onDelete={() => handleDeleteEducationalInstitution(index)}
                index={index}
              />
            ))}
            <div className="btn-center" style={{ flex: "0 0 100%" }}>
              <button
                className={`oxbtn btn-purple ${uneducated ? "disabled" : ""}`}
                onClick={handleAddAnotherEducationalInstitution}
                disabled={uneducated}
              >
                Add An Educational Institution
                <i className="bi bi-plus-lg btn-icon"></i>
              </button>
            </div>
          </div>

          {/* Employers/Congregations */}
          <div className="data-container">
            <div
              className="text-form-header"
              style={{ flex: "0 0 100%", textAlign: "left" }}
            >
              Employers/Substantial Services
            </div>
            <ol className="alphabet-list" start={2}>
              <li>{instructions?.instructionTwo}</li>
            </ol>
            <div style={{ flex: "0 0 100%" }}>
              {" "}
              If you have not had any employment or substantial volunteer work
              during the requested time period please contact us before
              proceeding.
            </div>
            {validationErrors.ecLength && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {validationErrors.ecLength}
              </div>
            )}
            <div style={{ flex: "0 0 100%" }}>
              {validationErrors.unemployed && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {validationErrors.unemployed}
                </div>
              )}
            </div>
            {employerCongregationList.map((employer, index) => (
              <EmployerCongregationForm
                key={index}
                employerCongregation={employer}
                validationErrors={validationErrors}
                onInputChange={handleInputChange}
                onDelete={() => handleDeleteEmployerCongregation(index)}
                index={index}
              />
            ))}
            <div className="btn-center" style={{ flex: "0 0 100%" }}>
              <button
                className={`oxbtn btn-purple ${unemployed ? "disabled" : ""}`}
                onClick={handleAddAnotherEmployerCongregation}
                disabled={unemployed}
              >
                Add An Employer/Substantial Services
                <i className="bi bi-plus-lg btn-icon"></i>
              </button>
            </div>
          </div>

          {/* Bishops/Religious */}
          <div className="data-container">
            <div
              className="text-form-header"
              style={{ flex: "0 0 100%", textAlign: "left" }}
            >
              Bishops/Religious Supervisors
            </div>
            <ol className="alphabet-list" start={3}>
              <li>{instructions?.instructionThree}</li>
            </ol>
            {validationErrors.brLength && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {validationErrors.brLength}
              </div>
            )}
            <div className="field-long" style={{ flex: "0 0 100%" }}>
              <div className="field-input" style={{ flex: "0 0 3%" }}>
                <input
                  className={`form-check-input ${
                    validationErrors.unreligious ? "is-invalid" : ""
                  }`}
                  type="checkbox"
                  checked={unreligious}
                  onChange={(e) => handleInputChange(e, "unreligious")}
                />
              </div>
              <label className="field-label" style={{ textWrap: "wrap" }}>
                This section does not apply to me based on instructions, part C,
                requirements.
              </label>
            </div>
            <div style={{ flex: "0 0 100%" }}>
              {validationErrors.unreligious && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {validationErrors.unreligious}
                </div>
              )}
            </div>
            {bishopReligiousList.map((bishop, index) => (
              <BishopsReligiousForm
                key={index}
                bishopsReligious={bishop}
                validationErrors={validationErrors}
                onInputChange={handleInputChange}
                onDelete={() => handleDeleteBishopReligious(index)}
                index={index}
              />
            ))}
            <div className="btn-center" style={{ flex: "0 0 100%" }}>
              <button
                className={`oxbtn btn-purple ${unreligious ? "disabled" : ""}`}
                onClick={handleAddAnotherBishopReligious}
                disabled={unreligious}
              >
                Add A Bishop/Religious Supervisor
                <i className="bi bi-plus-lg btn-icon"></i>
              </button>
            </div>
          </div>

          {/* Certification */}
          <div className="data-container">
            <div className="text-body" style={{ flex: "0 0 100%" }}>
              I hereby certify that I have listed all educational institutions I
              have attended in the past {referenceOrder?.years} years, all
              employers and congregations I have served in the past{" "}
              {referenceOrder?.years} years, (or my last two
              employers/congregations if I have been at my current position for
              more than {referenceOrder?.years} years), and all
              bishops/religious supervisors, past and present, who have had
              canonical authority over me.
            </div>
            <AuthorizationForm
              authText=""
              authorization={authorization}
              validationErrors={validationErrors}
              onInputChange={handleInputChange}
              type={""}
            />
          </div>

          <div className="action-buttons">
            <button
              className="oxbtn btn-grey"
              onClick={() => handleSaveDraft()}
            >
              Save and Return
            </button>
            <button className="oxbtn btn-green" onClick={() => handleSave()}>
              Submit
            </button>
          </div>
        </>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default ReferenceIdentificationForm;
